import { initializeBindings } from "https://static.cloud.coveo.com/atomic/v3/index.esm.js";
import {
	buildSearchBox,
	buildDidYouMean,
	buildSearchStatus,
	loadDidYouMeanActions,
	loadGenericAnalyticsActions
} from "https://static.cloud.coveo.com/headless/v3/headless.esm.js";

class CustomDidYouMean extends HTMLElement {

	async connectedCallback() {
		this.bindings = await initializeBindings(this);

		this.searchBox = buildSearchBox(this.bindings.engine);

		this.didYouMeanActions = loadDidYouMeanActions(this.bindings.engine);
		this.bindings.engine.dispatch(this.didYouMeanActions.enableAutomaticQueryCorrection());

		this.didYouMean = buildDidYouMean(this.bindings.engine, {
			options: {
				automaticallyCorrectQuery: true
			}
		});
		this.searchStatus = buildSearchStatus(this.bindings.engine);
		this.autoCorrectionAppliedFor = null;
		this.activeCorrection = null;
		this.stateSyncTimerId = null;
		this.stateSyncDelayMs = 60;
		this.variant = (this.dataset.variant || "full").toLowerCase();
		this.ensureContentRoot();

		this.pendingTitleTerm = null;
		this.pendingSearchStarted = false;

		this.didYouMean.subscribe(() => this.requestStateSync());
		this.searchStatus.subscribe(() => {
			this.trackPendingTitleSearch();
			this.requestStateSync();
		});
		this.requestStateSync();
	}

	requestStateSync() {
		if (this.stateSyncTimerId) {
			window.clearTimeout(this.stateSyncTimerId);
		}

		// DidYouMean and SearchStatus can emit multiple times per search; wait briefly for the latest state.
		this.stateSyncTimerId = window.setTimeout(() => {
			this.stateSyncTimerId = null;
			this.handleStateChange();
		}, this.stateSyncDelayMs);
	}

	handleStateChange() {
		const state = this.didYouMean.state || {};
		const statusState = this.searchStatus.state || {};
		// Mid-search, the status and correction state are still from the previous search and would overwrite the new title/query.
		if (statusState.isLoading) {
			return;
		}
		this.clearCorrectionUi();

		const correction = state.queryCorrection;
		const isNoResults = statusState.firstSearchExecuted && statusState.hasResults === false;

		this.cacheCorrection(state, correction, isNoResults);
		this.renderFromActiveCorrection(isNoResults);
		this.applyPendingTitleTerm();
	}

	trackPendingTitleSearch() {
		if (this.pendingTitleTerm && this.searchStatus.state.isLoading) {
			this.pendingSearchStarted = true;
		}
	}

	applyPendingTitleTerm() {
		if (!this.pendingTitleTerm) {
			return;
		}

		this.updateSearchPageTitleTerm(this.pendingTitleTerm);

		if (!this.pendingSearchStarted || this.searchStatus.state.isLoading) {
			return;
		}

		this.pendingTitleTerm = null;
		this.pendingSearchStarted = false;
	}

	cacheCorrection(state, correction, isNoResults) {
		if (!isNoResults && !correction && !state.wasCorrectedTo) {
			return;
		}

		// Automatic corrections expose the applied query via wasCorrectedTo rather than queryCorrection.
		const originalQuery = String(correction?.originalQuery || state.originalQuery || "").trim();
		const correctedQuery = String(correction?.correctedQuery || state.wasCorrectedTo || "").trim();
		if (!isNoResults && (!originalQuery || !correctedQuery)) {
			return;
		}

		this.activeCorrection = { originalQuery, correctedQuery };
		// No-results without a suggestion must not blank the search box or page title.
		if (!correctedQuery) {
			return;
		}
		if ((this.searchBox.state.value || "") !== correctedQuery) {
			this.searchBox.updateText(correctedQuery);
		}
		this.updateSearchPageTitleTerm(correctedQuery);
	}

	renderFromActiveCorrection(isNoResults) {
		if (!this.activeCorrection && !isNoResults) {
			this.clearCorrectionUi();
			return;
		}

		if (this.variant === "compact") {
			if (!isNoResults) {
				this.clearCorrectionUi();
				return;
			}
			this.renderNoResultsCorrection(this.activeCorrection);
			return;
		}

		if (isNoResults) {
			this.clearCorrectionUi();
			return;
		}

		this.renderCorrection(this.activeCorrection);
	}

	clearCorrectionUi() {
		this.autoCorrectionAppliedFor = null;
		this.activeCorrection = null;
		if (this.contentRoot) {
			this.contentRoot.replaceChildren();
		}
	}

	renderCorrection(correction) {
		const container = this.buildFromTemplate("full", {
			"corrected-query": correction.correctedQuery,
			"original-query": correction.originalQuery
		});
		if (!container) {
			this.clearCorrectionUi();
			return;
		}

		this.setRenderedContent(container);

		const button = this.querySelector('[data-action="search-original"]');
		if (button) {
			button.addEventListener("click", () => {
				this.bindings.engine.dispatch(this.didYouMeanActions.disableAutomaticQueryCorrection());
				this.searchBox.updateText(correction.originalQuery);
				this.updateSearchPageTitleTerm(correction.originalQuery);
				this.searchBox.submit();

				// Re-enable auto correction for subsequent regular searches.
				window.setTimeout(() => {
					this.bindings.engine.dispatch(this.didYouMeanActions.enableAutomaticQueryCorrection());
				}, 1200);
			});
		}
	}

	renderNoResultsCorrection(correction) {
		let container;

		if (correction?.correctedQuery) {
			container = this.buildFromTemplate("compact", {
				"corrected-query": correction?.correctedQuery,
				"original-query": correction?.originalQuery
			});
		} else {
			container = this.buildFromTemplate("compact-without-correction", {
				"original-query": correction?.originalQuery
			});
		}


		if (!container) {
			this.clearCorrectionUi();
			return;
		}

		this.setRenderedContent(container);

		const button = this.querySelector('[data-action="search-corrected"]');
		if (button) {
			button.addEventListener("click", () => {
				// The post-search state sync clears the title term, so keep it until the search completes.
				this.pendingTitleTerm = correction.correctedQuery;
				this.pendingSearchStarted = false;
				this.searchBox.updateText(correction.correctedQuery);
				this.updateSearchPageTitleTerm(correction.correctedQuery);
				this.searchBox.submit();

				const { logCustomEvent } = loadGenericAnalyticsActions(this.bindings.engine);

				const payload = {
					evt: "didyoumeanClick",
					type: "customdidyoumeanClick",
				}

				this.bindings.engine.dispatch(logCustomEvent(payload));
			});
		}
	}

	/**
	 * Delegates search-term title updates to the custom-search-bar instance when available.
	 */
	updateSearchPageTitleTerm(searchTerm) {
		const searchBarElement = document.querySelector("custom-search-bar");

		if (searchBarElement && typeof searchBarElement.appendSearchTermToPageTitleTag === "function") {
			searchBarElement.appendSearchTermToPageTitleTag(searchTerm);
		}
	}

	buildFromTemplate(role, values = {}) {
		const template = this.querySelector(`:scope > template[data-role="${role}"]`);
		if (!(template instanceof HTMLTemplateElement)) {
			console.warn(`custom-did-you-mean missing template for role: ${role}`);
			return null;
		}

		const fragment = template.content.cloneNode(true);
		Object.entries(values).forEach(([part, text]) => {
			fragment.querySelectorAll(`[data-part="${part}"]`).forEach((node) => {
				node.textContent = String(text);
			});
		});

		const container = document.createElement("div");
		container.append(fragment);
		return container;
	}

	ensureContentRoot() {
		this.contentRoot = this.querySelector(':scope > .did-you-mean-content');
		if (!this.contentRoot) {
			this.contentRoot = document.createElement("div");
			this.contentRoot.className = "did-you-mean-content";
			this.prepend(this.contentRoot);
		}
	}

	setRenderedContent(node) {
		this.ensureContentRoot();
		this.contentRoot.replaceChildren(node);
	}
}

if (!window.customElements.get("custom-did-you-mean")) {
	window.customElements.define("custom-did-you-mean", CustomDidYouMean);
}